import { type FocusEvent, type MouseEvent, useCallback, useEffect, useRef } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';

import { type WrapperRenderProps } from '../../../types';

export function useAssignRefsAndAriaLabels() {
	const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
	const triggerPropsRef = useRef<WrapperRenderProps['triggerProps']>(undefined);

	const onMouseEnter = useCallback((e: Event) => {
		triggerPropsRef.current?.onMouseEnter?.(e as unknown as MouseEvent<Element>);
	}, []);

	const onMouseLeave = useCallback((e: Event) => {
		triggerPropsRef.current?.onMouseLeave?.(e as unknown as MouseEvent<Element>);
	}, []);

	const onBlur = useCallback((e: Event) => {
		triggerPropsRef.current?.onBlur?.(e as unknown as FocusEvent<Element>);
	}, []);

	const onFocus = useCallback((e: Event) => {
		triggerPropsRef.current?.onFocus?.(e as unknown as FocusEvent<Element>);
	}, []);

	useEffect(function assignAriaLabels() {
		const itemRef = ref.current;
		const triggerRef = triggerPropsRef.current;

		if (itemRef && triggerRef) {
			triggerRef['aria-controls'] !== undefined &&
				itemRef.setAttribute('aria-controls', triggerRef['aria-controls']);
			triggerRef['aria-haspopup'] !== undefined &&
				itemRef.setAttribute('aria-haspopup', triggerRef['aria-haspopup'].toString());
			triggerRef['aria-expanded'] !== undefined &&
				itemRef.setAttribute('aria-expanded', triggerRef['aria-expanded'].toString());
			triggerRef['data-ds--level'] !== undefined &&
				itemRef.setAttribute('data-ds--level', triggerRef['data-ds--level']);
		}
	}, []);

	useEffect(
		function bindMouseEvents() {
			const itemRef = ref.current;
			let unbind: UnbindFn;
			if (itemRef) {
				unbind = bindAll(itemRef, [
					{
						type: 'mouseenter',
						listener: onMouseEnter,
						options: { capture: true },
					},
					{
						type: 'mouseleave',
						listener: onMouseLeave,
						options: { capture: true },
					},
					{
						type: 'blur',
						listener: onBlur,
						options: { capture: true },
					},
					{
						type: 'focus',
						listener: onFocus,
						options: { capture: true },
					},
				]);
			}

			return () => {
				unbind?.();
			};
		},
		[onBlur, onFocus, onMouseEnter, onMouseLeave],
	);

	return { ref, triggerPropsRef };
}
